import { useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { ApiError } from '@/lib/api'
import { Button, Input } from '@/design/components'
import { useAuth } from './AuthProvider'
import { Logo } from './Logo'

interface LocationState {
  from?: string
}

/** Ekran logowania — granat + złoto, bez ozdobników. */
export function LoginPage() {
  const { status, login } = useAuth()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (status === 'authenticated') {
    const target = (location.state as LocationState | null)?.from ?? '/'
    return <Navigate to={target} replace />
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(email.trim(), password)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 401 ? 'Nieprawidłowy e-mail lub hasło.' : err.userMessage)
      } else {
        setError('Nie udało się zalogować.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-800 px-6 py-10">
      <div className="w-full max-w-[380px]">
        <div className="mb-7 flex justify-center">
          <Logo />
        </div>

        <form
          onSubmit={onSubmit}
          className="overflow-hidden rounded-lg border-t-[3px] border-gold bg-card px-7 py-7 shadow-modal"
        >
          <h1 className="t-h2 mb-1">Logowanie</h1>
          <p className="mb-6 text-small text-muted">System obsługi kancelarii — dostęp dla pracowników.</p>

          <div className="flex flex-col gap-4">
            <Input
              label="Adres e-mail"
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="imie.nazwisko@kancelaria.local"
            />
            <Input
              label="Hasło"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
            />
          </div>

          {error ? (
            <p role="alert" className="mt-4 rounded border border-danger/30 bg-chip-danger-bg px-3 py-2 text-small text-chip-danger-fg">
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="primary" block loading={busy} className="mt-6">
            Zaloguj się
          </Button>
        </form>

        <p className="mt-5 text-center text-micro text-sidebar-muted">
          Problem z dostępem? Skontaktuj się z administratorem kancelarii.
        </p>
      </div>
    </div>
  )
}
